import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { PanAppState } from '@panamax/app-state';
import {
  BankAccount,
  PaymentPreferencesPageViewModel,
} from '@payments/models/payment-preferences-page-view-model';
import { getPaymentPreferencesPageViewModel } from '@payments/selectors/payments.selector';
import { ModalFilterService } from '@payments/services/payments-modal.service';
import { FEATURES } from '@shared/constants/splitio-features';
import {
  BankStoreService,
  Filter,
  SortDirection,
  SortFilter,
} from '@usf/ngrx-payment';
import { Status } from '@usf/payment-types';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';

export interface PaymentPreferenceFilter {
  filterName: string;
  value: string | number | string[] | number[];
}

export enum PaymentPreferenceFilterIds {
  type = 'isPrimary',
  customerName = 'customerName',
}

export enum PaymentPreferenceSortFields {
  accountNumber = 'last4AccountNumber',
  bank = 'bankName',
  customerName = 'customerName',
  nickname = 'nickName',
  routingNumber = 'routingNumber',
  startDate = 'autoDeductStartDate',
  type = 'isPrimary',
}

export enum BankAccountTypes {
  autopay = 'autopay',
  manual = 'manual',
}

@Injectable({
  providedIn: 'root',
})
export class PaymentPreferencesService implements ModalFilterService {
  readonly initialSort: SortFilter[] = [
    {
      fieldName: PaymentPreferenceSortFields.customerName,
      sortValue: SortDirection.ASC,
    },
  ];
  private filtersSubject = new BehaviorSubject<PaymentPreferenceFilter[]>([]);
  private sortSubject = new BehaviorSubject<SortFilter[]>(this.initialSort);

  constructor(
    private store: Store,
    private panAppState: PanAppState,
    private bankStoreService: BankStoreService,
  ) {}

  getPaymentPreferencesPageViewModel$(): Observable<PaymentPreferencesPageViewModel> {
    this.clearFiltersAndResetSort();
    return combineLatest([
      this.store.select(getPaymentPreferencesPageViewModel),
      this.panAppState.feature$([FEATURES.split_user_payments_autopay.name]),
      this.panAppState.feature$([
        FEATURES.split_dynamic_payments_routing_validation.name,
      ]),
      this.filtersSubject,
      this.sortSubject,
    ]).pipe(
      map(
        ([vm, isAutopayEnabled, isBankRoutingValidationOn, filters, sorts]) => {
          const autopayBankAccounts = this.sortBankAccounts(
            vm.autopayBankAccounts,
            sorts,
          );
          const manualDeductBankAccounts = this.sortBankAccounts(
            vm.manualDeductBankAccounts,
            sorts,
          );
          const filterCount = this.countActiveFilters(filters);

          return {
            ...vm,
            autopayBankAccounts,
            manualDeductBankAccounts,
            isAutopayEnabled,
            isBankRoutingValidationOn,
            filters,
            filterCount,
            sorts,
          };
        },
      ),
    );
  }

  private countActiveFilters(filters: Filter[]): number {
    return (
      filters &&
      filters.reduce((count, filter) => {
        if (Array.isArray(filter.value)) {
          return count + filter.value.length;
        }
        if (!!filter.value) {
          return count + 1;
        }
        return count;
      }, 0)
    );
  }

  getBankDetails(): void {
    const filters = this.filtersSubject.getValue();
    let customers: { customerNumber: number }[] = [];
    let isPrimary = false;
    filters.forEach(filter => {
      switch (filter.filterName) {
        case PaymentPreferenceFilterIds.customerName:
          customers = (filter.value as number[]).map(value => {
            return { customerNumber: value };
          });
          break;
        case PaymentPreferenceFilterIds.type:
          isPrimary = Boolean(filter.value as number);
          break;
      }
    });
    this.bankStoreService.fetchBanksDetails(customers, isPrimary);
  }

  getUniqueBankAccounts(): void {
    this.bankStoreService.fetchUniqueBankAccounts();
  }

  private sortBankAccounts(
    bankAccounts: BankAccount[],
    sortFilters: SortFilter[],
  ): BankAccount[] {
    let newBankAccounts = [...bankAccounts];

    let mutableSortFilters = [...sortFilters];
    if (newBankAccounts.length > 0 && sortFilters.length > 0) {
      mutableSortFilters.reverse().forEach(sortFilter => {
        const fieldName = sortFilter.fieldName;
        switch (fieldName) {
          case PaymentPreferenceSortFields.accountNumber:
          case PaymentPreferenceSortFields.bank:
          case PaymentPreferenceSortFields.customerName:
          case PaymentPreferenceSortFields.nickname:
          case PaymentPreferenceSortFields.routingNumber:
            newBankAccounts.sort((a, b) =>
              (a[fieldName] ?? '')
                .toLowerCase()
                .localeCompare((b[fieldName] ?? '').toLowerCase()),
            );
            break;
          case PaymentPreferenceSortFields.startDate:
          case PaymentPreferenceSortFields.type:
            newBankAccounts.sort(
              (a, b) => (a[fieldName] as number) - (b[fieldName] as number),
            );
            break;
        }

        if (sortFilter.sortValue === SortDirection.DESC) {
          newBankAccounts.reverse();
        }
      });
    }

    return newBankAccounts;
  }

  // ModalFilterService

  getFiltersAndSortInformation$(): Observable<{
    filters: Filter[];
    sort: SortFilter[];
  }> {
    return combineLatest([this.filtersSubject, this.sortSubject]).pipe(
      map(([filters, sort]) => {
        return { filters, sort };
      }),
    );
  }

  getStatusOptions(): Map<Status, string> {
    return new Map([]);
  }

  setFilter(
    filters: PaymentPreferenceFilter[],
    restrictToOpen?: boolean,
  ): void {
    this.filtersSubject.next(filters);
  }

  setSort(sortOrder: SortFilter[]) {
    this.sortSubject.next(sortOrder);
  }

  clearFiltersAndResetSort() {
    this.filtersSubject.next([]);
    this.sortSubject.next(this.initialSort);
  }
}
